import React, { useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';

import { ensureHttps, getFormattedStackVersion, normaliseUrl } from '@cloud-wave/neon-common-lib';

import { TLogEventValue, logger } from 'lib/common/components/LoggerController';

import { ImageType } from 'lib/common/constants/imageBlockFields';

import { TConfig } from 'lib/common/types/Config';

import { getDefaultBrandImageUrl, getFileUrl } from '../utils';
import ConfigContext, { configObj } from './Context';

type Props = {
  children: React.ReactNode;
};

const fetchConfig = async (): Promise<TConfig> => {
  const configResponse = await fetch(
    `${configObj.ADMIN_SERVICE_URL}/cfg/unsecure/${configObj.TENANT_ID}/${configObj.DEPLOYMENT_ENVIRONMENT}`
  );

  if (configResponse.status > 299) {
    throw new Error(`Failed to fetch config: ${configResponse.status}`);
  }

  const configJson = (await configResponse.json()).data;
  const helpDocsUrl = configJson.BRAND?.helpDocsUrl || configObj.BRAND.helpDocsUrl;
  const normalisedHelpDocsUrl = normaliseUrl(ensureHttps(helpDocsUrl));

  return {
    ...configObj,
    ...configJson,
    BRAND: {
      ...configJson.BRAND,
      productName: configJson.BRAND?.productName || configObj.BRAND.productName,
      helpDocsUrl: normalisedHelpDocsUrl,
    },
  };
};

const ConfigProvider = ({ children }: Props) => {
  const {
    data: config = configObj,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['config', configObj.TENANT_ID, configObj.DEPLOYMENT_ENVIRONMENT],
    queryFn: fetchConfig,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (config && config !== configObj) {
      logger.info(
        `~~~ [${config.BRAND.productName} Version ${
          getFormattedStackVersion(config.STACK_VERSION).presentation
        }] ~~~` as TLogEventValue
      );

      document
        .querySelector(`link[rel~='icon']`)
        ?.setAttribute(
          'href',
          config.BRAND.favicon?.fileKey
            ? getFileUrl(config.BRAND.favicon?.fileKey, config)
            : getDefaultBrandImageUrl(ImageType.FAVICON)
        );

      document.title = config.BRAND.productName;
    }
  }, [config]);

  if (isLoading) {
    return null;
  }

  return <ConfigContext.Provider value={{ config, notFound: isError }}>{children}</ConfigContext.Provider>;
};

export default ConfigProvider;
